import { Alert, Button, Card, Form, Input, Space, Typography } from "antd";
import type { DeviceVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { DEVICE_TYPE, TRANSPORT_TYPE, ONLINE } from "@/meta/logistics/device";
import { Grid as ZhGrid } from "zhihao-ui-react";

export interface DeviceEditCorePayload {
    name: string;
    serialNo: string;
    dataCard: string;
    type: number;
    transportType: number;
    masterId?: string;
    position?: string;
    installerInfo?: unknown;
    installDate?: string;
    installSite?: string;
    referrerId?: number;
}

export interface DeviceManagementEditPageProps {
    deviceId?: number | string;
    loadDevice?: (id: number | string) => Promise<DeviceVo | undefined>;
    updateDevice?: (id: number | string, payload: DeviceEditCorePayload) => Promise<unknown>;
}

/** 复用 Vue 设备编辑详情接口。 */
export async function loadDeviceForEdit(id: number | string) {
    const response = await shippingRequestHttp.service.get<DeviceVo>(`/device/vo/${id}`);
    return response.data;
}

/** 复用 Vue 设备编辑更新接口。 */
export async function updateDeviceManagement(id: number | string, payload: DeviceEditCorePayload) {
    const response = await shippingRequestHttp.service.put(`/device/${id}`, payload);
    return response.data;
}

/** 物流管理-设备编辑，覆盖 Vue Edit 分支的核心回显、只读和更新行为。 */
export function DeviceManagementEditPage({
    deviceId,
    loadDevice = loadDeviceForEdit,
    updateDevice = updateDeviceManagement
}: DeviceManagementEditPageProps) {
    const routeParams = useParams<{ id: string }>();
    const navigate = useNavigate();
    const resolvedId = deviceId ?? routeParams.id ?? "";
    const [form] = Form.useForm<DeviceEditCorePayload>();
    const [errorMessage, setErrorMessage] = useState("");
    const [onlineState, setOnlineState] = useState<number>();

    useEffect(() => {
        let disposed = false;
        void loadDevice(resolvedId)
            .then(detail => {
                if (disposed || !detail?.device) return;
                form.setFieldsValue({
                    name: detail.device.name,
                    serialNo: detail.device.serialNo,
                    dataCard: detail.device.dataCard,
                    type: detail.device.type,
                    transportType: TRANSPORT_TYPE.SHIP.code,
                    masterId: detail.device.masterId,
                    position: detail.device.position,
                    installerInfo: detail.device.installerInfo,
                    installDate: detail.device.installDate,
                    installSite: detail.device.installSite,
                    referrerId: detail.device.referrerId
                });
                setOnlineState(detail.device.online);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("设备详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [form, loadDevice, resolvedId]);

    const submitForm = async (values: DeviceEditCorePayload) => {
        try {
            // Vue 直接提交完整 deviceEditForm，保留关联船舶和安装信息等可选字段。
            const payload: DeviceEditCorePayload = {
                name: values.name,
                serialNo: values.serialNo,
                dataCard: values.dataCard,
                type: values.type || DEVICE_TYPE.CAMERA.code,
                transportType: values.transportType || TRANSPORT_TYPE.SHIP.code
            };
            if (values.masterId !== undefined) payload.masterId = values.masterId;
            if (values.position !== undefined) payload.position = values.position;
            if (values.installerInfo !== undefined) payload.installerInfo = values.installerInfo;
            if (values.installDate !== undefined) payload.installDate = values.installDate;
            if (values.installSite !== undefined) payload.installSite = values.installSite;
            if (values.referrerId !== undefined) payload.referrerId = values.referrerId;
            await updateDevice(resolvedId, payload);
            navigate(-1);
        } catch {
            setErrorMessage("设备更新失败");
        }
    };

    return (
        <section aria-label="device-management-edit-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title level={4}>编辑设备</Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Form form={form} layout="vertical" onFinish={values => void submitForm(values)}>
                    <Form.Item name="type" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="transportType" hidden>
                        <Input />
                    </Form.Item>
                    <ZhGrid cols={4} gap={[30, 2]}>
                        <Form.Item label="名称" name="name" rules={[{ required: true, message: "请输入设备名称" }]}>
                            <Input aria-label="名称" maxLength={40} placeholder="请输入设备名称" />
                        </Form.Item>
                        <Form.Item label="类型">
                            <Input disabled value={DEVICE_TYPE.CAMERA.intro || DEFAULT_VALUE} />
                        </Form.Item>
                        <Form.Item label="序列号" name="serialNo">
                            <Input aria-label="序列号" disabled />
                        </Form.Item>
                        <Form.Item
                            label="ICCID"
                            name="dataCard"
                            rules={[{ required: true, len: 20, message: "请输入20个字符的ICCID" }]}
                        >
                            <Input aria-label="ICCID" maxLength={20} placeholder="请输入ICCID" />
                        </Form.Item>
                        <Form.Item label="是否在线">
                            <Input disabled value={ONLINE.getIntroByCode(onlineState) || DEFAULT_VALUE} />
                        </Form.Item>
                        <Form.Item label="关联船舶" name="masterId">
                            <Input placeholder="请输入船舶 ID" />
                        </Form.Item>
                        <Form.Item
                            dependencies={["masterId"]}
                            label="安装位置"
                            name="position"
                            rules={[
                                ({ getFieldValue }) => ({
                                    required: Boolean(getFieldValue("masterId")),
                                    message: "请输入安装位置"
                                })
                            ]}
                        >
                            <Input maxLength={20} placeholder="请输入安装位置" />
                        </Form.Item>
                        <Form.Item label="安装人" name="installerInfo">
                            <Input placeholder="请搜索安装人" />
                        </Form.Item>
                        <Form.Item label="安装日期" name="installDate">
                            <Input aria-label="安装日期" placeholder="请选择安装日期" type="date" />
                        </Form.Item>
                        <Form.Item label="安装地点" name="installSite">
                            <Input maxLength={32} placeholder="请输入安装地点" />
                        </Form.Item>
                        <Form.Item label="推介人" name="referrerId">
                            <Input aria-label="推介人" placeholder="请输入推介人账号ID" type="number" />
                        </Form.Item>
                    </ZhGrid>
                    <Space>
                        <Button htmlType="submit" type="primary">
                            提交
                        </Button>
                        <Button onClick={() => navigate(-1)}>返回</Button>
                    </Space>
                </Form>
            </Card>
        </section>
    );
}
